import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Camera, Plus, Instagram, Facebook, Youtube, Twitter, ChevronDown } from 'lucide-react';
import { SectionCard } from '../components/editor/SectionCard';
import { DevicePreview } from '../components/editor/DevicePreview';
interface EditorCard {
  id: string;
  title: string;
  url: string;
  description: string;
  image: string;
  enabled: boolean;
  price?: string;
  status?: string;
}
interface EditorSection {
  id: string;
  type: 'listings-videos' | 'custom-links';
  title: string;
  description: string;
  layout: string;
  cards: EditorCard[];
  collapsed: boolean;
}
export function DashboardEditProfile() {
  const [socialLinks, setSocialLinks] = useState([{
    id: 1,
    platform: 'Instagram',
    url: 'instagram.com/marcuschen',
    enabled: true,
    icon: Instagram
  }, {
    id: 2,
    platform: 'Facebook',
    url: 'facebook.com/marcuschen',
    enabled: true,
    icon: Facebook
  }, {
    id: 3,
    platform: 'YouTube',
    url: 'youtube.com/@marcuschen',
    enabled: true,
    icon: Youtube
  }, {
    id: 4,
    platform: 'X (Twitter)',
    url: 'x.com/marcuschen',
    enabled: false,
    icon: Twitter
  }]);
  const [sections, setSections] = useState<EditorSection[]>([{
    id: 'section-1',
    type: 'listings-videos',
    title: 'Featured Listings',
    description: 'My current and upcoming properties',
    layout: 'small-card',
    collapsed: false,
    cards: [{
      id: 'card-1',
      title: '1428 Pacific Ave',
      url: 'https://zillow.com/listing-1',
      description: '4 bed · 3.5 bath · 2,850 sqft',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&h=200&fit=crop',
      enabled: true,
      price: '$2.45M',
      status: 'Active'
    }, {
      id: 'card-2',
      title: '850 Marina Blvd',
      url: 'https://zillow.com/listing-2',
      description: '3 bed · 2 bath · 2,100 sqft',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=200&fit=crop',
      enabled: true,
      price: '$3.1M',
      status: 'Coming Soon'
    }, {
      id: 'card-3',
      title: '245 Cole St',
      url: 'https://zillow.com/listing-3',
      description: '3 bed · 2.5 bath · 1,950 sqft',
      image: 'https://images.unsplash.com/photo-1600607687931-cebf004f560c?w=200&h=200&fit=crop',
      enabled: true,
      price: '$1.9M',
      status: 'Open House'
    }]
  }, {
    id: 'section-2',
    type: 'custom-links',
    title: 'Resources',
    description: 'Helpful links and tools',
    layout: 'list',
    collapsed: false,
    cards: [{
      id: 'card-4',
      title: 'Book a Consultation',
      url: 'calendly.com/marcuschen',
      description: 'Schedule a free 30-minute consultation',
      image: '',
      enabled: true
    }, {
      id: 'card-5',
      title: 'First-Time Buyer Guide',
      url: 'marcuschen.com/guide',
      description: 'Everything you need to know about buying your first home',
      image: '',
      enabled: true
    }]
  }]);
  const [showAddSectionMenu, setShowAddSectionMenu] = useState(false);
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const handleSectionUpdate = (id: string, updates: Partial<EditorSection>) => {
    setSections(prev => prev.map(section => section.id === id ? {
      ...section,
      ...updates
    } : section));
  };
  const handleSectionDelete = (id: string) => {
    setSections(prev => prev.filter(section => section.id !== id));
  };
  const handleCardUpdate = (sectionId: string, cardId: string, updates: Partial<EditorCard>) => {
    setSections(prev => prev.map(section => section.id === sectionId ? {
      ...section,
      cards: section.cards.map(card => card.id === cardId ? {
        ...card,
        ...updates
      } : card)
    } : section));
  };
  const handleCardDelete = (sectionId: string, cardId: string) => {
    setSections(prev => prev.map(section => section.id === sectionId ? {
      ...section,
      cards: section.cards.filter(card => card.id !== cardId)
    } : section));
  };
  const handleCardAdd = (sectionId: string, cardData: Omit<EditorCard, 'id' | 'enabled'>) => {
    setSections(prev => prev.map(section => section.id === sectionId ? {
      ...section,
      cards: [...section.cards, {
        ...cardData,
        id: `card-${Date.now()}`,
        enabled: true
      }]
    } : section));
  };
  const handleCardReorder = (sectionId: string, fromIndex: number, toIndex: number) => {
    setSections(prev => prev.map(section => {
      if (section.id !== sectionId) return section;
      const newCards = [...section.cards];
      const [movedCard] = newCards.splice(fromIndex, 1);
      newCards.splice(toIndex, 0, movedCard);
      return {
        ...section,
        cards: newCards
      };
    }));
  };
  const handleAddSection = (type: 'listings-videos' | 'custom-links') => {
    const newSection: EditorSection = {
      id: `section-${Date.now()}`,
      type,
      title: type === 'listings-videos' ? 'New Listings Section' : 'New Links Section',
      description: 'Add a description',
      layout: type === 'listings-videos' ? 'small-card' : 'list',
      collapsed: false,
      cards: []
    };
    setSections(prev => [...prev, newSection]);
    setShowAddSectionMenu(false);
  };
  const handleSectionDragStart = (e: React.DragEvent, sectionId: string) => {
    setDraggedSectionId(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleSectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleSectionDrop = (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    if (!draggedSectionId || draggedSectionId === targetSectionId) {
      setDraggedSectionId(null);
      return;
    }
    const fromIndex = sections.findIndex(s => s.id === draggedSectionId);
    const toIndex = sections.findIndex(s => s.id === targetSectionId);
    if (fromIndex !== -1 && toIndex !== -1) {
      const newSections = [...sections];
      const [movedSection] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, movedSection);
      setSections(newSections);
    }
    setDraggedSectionId(null);
  };
  return <div className="flex h-full min-h-screen" data-id="element-455">
      <div className="flex-1 max-w-3xl border-r border-brand-light bg-brand-offwhite" data-id="element-456">
        <div className="p-8" data-id="element-457">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-dark mb-8 transition-colors" data-id="element-458">
            <ArrowLeft size={16} data-id="element-459" />
            Back to profile
          </Link>

          <h1 className="text-3xl font-serif font-semibold text-brand-dark mb-8" data-id="element-460">
            Edit Profile
          </h1>

          <section className="bg-white rounded-2xl border border-brand-light shadow-sm p-6 mb-8" data-id="element-461">
            <h2 className="text-lg font-medium text-brand-dark mb-6" data-id="element-462">
              Profile Details
            </h2>

            <div className="flex items-center gap-6 mb-8" data-id="element-463">
              <div className="relative w-24 h-24 rounded-full overflow-hidden border border-brand-light group cursor-pointer" data-id="element-464">
                <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Marcus Chen" className="w-full h-full object-cover" data-id="element-465" />
                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-466">
                  <Camera size={20} className="text-white mb-1" data-id="element-467" />
                  <span className="text-[10px] text-white font-medium" data-id="element-468">
                    Change
                  </span>
                </div>
              </div>
              <div className="flex-1" data-id="element-469">
                <button className="px-4 py-2 bg-brand-offwhite border border-brand-light hover:bg-brand-sage rounded-xl text-sm font-medium text-brand-dark transition-colors" data-id="element-470">
                  Upload new image
                </button>
                <p className="text-xs text-brand-muted mt-2" data-id="element-471">
                  JPG, GIF or PNG. Max size of 5MB.
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-472">
              <div className="grid grid-cols-2 gap-4" data-id="element-473">
                <div data-id="element-474">
                  <label className="block text-xs font-medium text-brand-muted mb-1.5" data-id="element-475">
                    Display Name
                  </label>
                  <input type="text" defaultValue="Marcus Chen" className="w-full bg-white border border-brand-light rounded-xl px-4 py-3 text-sm focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 outline-none transition-colors" data-id="element-476" />
                </div>
                <div data-id="element-477">
                  <label className="block text-xs font-medium text-brand-muted mb-1.5" data-id="element-478">
                    Title
                  </label>
                  <input type="text" defaultValue="Senior real estate advisor" className="w-full bg-white border border-brand-light rounded-xl px-4 py-3 text-sm focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 outline-none transition-colors" data-id="element-479" />
                </div>
              </div>

              <div data-id="element-480">
                <label className="block text-xs font-medium text-brand-muted mb-1.5" data-id="element-481">
                  Brokerage
                </label>
                <input type="text" defaultValue="Compass Real Estate · The Chen Group" className="w-full bg-white border border-brand-light rounded-xl px-4 py-3 text-sm focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 outline-none transition-colors" data-id="element-482" />
              </div>

              <div data-id="element-483">
                <label className="block text-xs font-medium text-brand-muted mb-1.5" data-id="element-484">
                  Short URL
                </label>
                <div className="flex items-center bg-white border border-brand-light rounded-xl overflow-hidden focus-within:border-brand-forest focus-within:ring-1 focus-within:ring-brand-forest/20 transition-colors" data-id="element-485">
                  <span className="px-4 py-3 bg-brand-offwhite border-r border-brand-light text-sm text-brand-muted font-mono" data-id="element-486">
                    eaves.pro/
                  </span>
                  <input type="text" defaultValue="marcuschen" className="flex-1 px-4 py-3 text-sm outline-none font-mono" data-id="element-487" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-488">
                <div data-id="element-489">
                  <label className="block text-xs font-medium text-brand-muted mb-1.5" data-id="element-490">
                    Location
                  </label>
                  <input type="text" defaultValue="San Francisco Bay Area" className="w-full bg-white border border-brand-light rounded-xl px-4 py-3 text-sm focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 outline-none transition-colors" data-id="element-491" />
                </div>
                <div data-id="element-492">
                  <label className="block text-xs font-medium text-brand-muted mb-1.5" data-id="element-493">
                    DRE Number
                  </label>
                  <input type="text" defaultValue="02145678" className="w-full bg-white border border-brand-light rounded-xl px-4 py-3 text-sm focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 outline-none transition-colors font-mono" data-id="element-494" />
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-brand-light shadow-sm p-6 mb-8" data-id="element-495">
            <div className="flex items-center justify-between mb-6" data-id="element-496">
              <h2 className="text-lg font-medium text-brand-dark" data-id="element-497">
                Social Links
              </h2>
              <button className="inline-flex items-center gap-1.5 text-sm text-brand-forest font-medium hover:text-brand-forestHover transition-colors" data-id="element-498">
                <Plus size={16} data-id="element-499" />
                Add social
              </button>
            </div>

            <div className="space-y-3" data-id="element-500">
              {socialLinks.map(link => {
              const Icon = link.icon;
              return <div key={link.id} className="flex items-center gap-4 p-4 bg-brand-offwhite/50 border border-brand-light rounded-xl" data-id="element-501">
                    <div className="w-8 h-8 rounded-full bg-white border border-brand-light flex items-center justify-center shrink-0" data-id="element-502">
                      <Icon size={14} className="text-brand-muted" data-id="element-503" />
                    </div>
                    <div className="flex-1" data-id="element-504">
                      <input type="text" defaultValue={link.url} className="w-full bg-transparent text-sm outline-none text-brand-dark placeholder:text-brand-light" placeholder={`${link.platform} URL`} data-id="element-505" />
                    </div>
                    <button className={`w-10 h-6 rounded-full transition-colors relative ${link.enabled ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => {
                  const newLinks = [...socialLinks];
                  const idx = newLinks.findIndex(l => l.id === link.id);
                  newLinks[idx].enabled = !newLinks[idx].enabled;
                  setSocialLinks(newLinks);
                }} data-id="element-506">
                      <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-transform ${link.enabled ? 'left-5' : 'left-1'}`} data-id="element-507"></div>
                    </button>
                  </div>;
            })}
            </div>
          </section>

          <section className="mb-8" data-id="element-508">
            <div className="flex items-center justify-between mb-6" data-id="element-509">
              <h2 className="text-xl font-serif font-medium text-brand-dark" data-id="element-510">
                Your Sections
              </h2>
              <div className="relative" data-id="element-511">
                <button onClick={() => setShowAddSectionMenu(!showAddSectionMenu)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-forest text-white rounded-full text-sm font-medium hover:bg-brand-forestHover transition-colors" data-id="element-512">
                  <Plus size={16} data-id="element-513" />
                  Add Section
                  <ChevronDown size={14} data-id="element-514" />
                </button>

                {showAddSectionMenu && <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-brand-light shadow-premium py-2 z-10" data-id="element-515">
                    <button onClick={() => handleAddSection('listings-videos')} className="w-full px-4 py-2 text-left text-sm text-brand-dark hover:bg-brand-offwhite transition-colors" data-id="element-516">
                      + Listings & Videos
                    </button>
                    <button onClick={() => handleAddSection('custom-links')} className="w-full px-4 py-2 text-left text-sm text-brand-dark hover:bg-brand-offwhite transition-colors" data-id="element-517">
                      + Custom Links
                    </button>
                  </div>}
              </div>
            </div>

            <div className="space-y-4" data-id="element-518">
              {sections.map(section => <SectionCard key={section.id} section={section} onUpdate={handleSectionUpdate} onDelete={handleSectionDelete} onCardUpdate={handleCardUpdate} onCardDelete={handleCardDelete} onCardAdd={handleCardAdd} onCardReorder={handleCardReorder} onDragStart={handleSectionDragStart} onDragOver={handleSectionDragOver} onDrop={handleSectionDrop} isDragging={draggedSectionId === section.id} data-id="element-519" />)}
            </div>
          </section>

          <div className="flex justify-end" data-id="element-520">
            <button className="px-6 py-3 bg-brand-forest hover:bg-brand-forestHover text-white font-medium rounded-xl transition-colors shadow-sm" data-id="element-521">
              Save changes
            </button>
          </div>
        </div>
      </div>

      <DevicePreview sections={sections} socialLinks={socialLinks} data-id="element-522" />
    </div>;
}